// ─── Lightweight visitor/usage tracking client ───────────────────────────────
// Sends pageview/event beacons to POST /api/v1/analytics/track.
// Uses a per-browser-tab sessionId stored in sessionStorage so a single
// browsing session is grouped together server-side.

const BASE = import.meta.env.VITE_API_URL ?? '/api/v1';
const SOURCE = 'admin';
const SESSION_KEY = 'kb_track_session_id';

function getSessionId() {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

function getToken() {
  try { return localStorage.getItem('kb_admin_token'); } catch { return null; }
}

function send(payload) {
  const body = JSON.stringify({
    sessionId: getSessionId(),
    source: SOURCE,
    language: navigator.language,
    screenWidth: window.screen?.width,
    screenHeight: window.screen?.height,
    referrer: document.referrer || undefined,
    ...payload,
  });

  const url = `${BASE}/analytics/track`;
  const token = getToken();

  // Prefer sendBeacon for unload-safety, but it can't carry custom headers,
  // so only use it when no auth is needed (anonymous beacons).
  if (!token && navigator.sendBeacon) {
    try {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      return;
    } catch { /* fall through to fetch */ }
  }

  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body,
    keepalive: true,
  }).catch(() => { /* tracking is best-effort — never block the UI */ });
}

/** Track a page/section view (call whenever the admin navigates) */
export function trackPageview(path) {
  send({ eventType: 'pageview', path });
}

/** Track a generic event (button click, form submit, search, upload, etc.) */
export function trackEvent(eventType, path, label, meta) {
  send({ eventType, path, label, meta });
}
